/* eslint-disable */
import React, {startTransition, useEffect, useState} from "react"
import {useModalsCloseObservable} from "@next/eventservice"
import ToolTip from "@next/tooltipv2"
import {Container, Title, CloseButton, CloseImage, Header, Content, Link, UserSection, Capitalize} from "./components"
import env from "../../config/env"
import {
    getHardLogoutPromptDisplayed,
    getIdentifiedUser,
    getFavouriteLoginPromptDisplayed,
    getSoftLoginFirstname,
} from "../../utils/getWindowNextFavourites"
import {FavouriteState} from "../../models/Favourites"
import {getAccountPage, getRegisterPage} from "../../urls"
import {TextModel} from "../../models/Text"
import {PLP_ENTRYPOINT} from "../../config/constants"
import {useIsOnClient} from "../../hooks/useIsOnClient"

export interface FavoritesToolTipProps {
    baseUrl: string
    isProductFavourited: boolean
    favState: FavouriteState
    referenceElement: HTMLElement | null
    text: TextModel
    setFavClicked: (value: boolean) => void
}

const FavoritesToolTip = ({
    baseUrl,
    isProductFavourited,
    favState,
    referenceElement,
    text,
    setFavClicked,
}: FavoritesToolTipProps) => {
    const [toolTipVisible, setToolTipVisible] = useState(false)
    const {isOnClient} = useIsOnClient()

    const showAuthenticatedToolTip = isOnClient && !getHardLogoutPromptDisplayed() && getIdentifiedUser()
    const showAnonymousToolTip = isOnClient && !getFavouriteLoginPromptDisplayed() && !getSoftLoginFirstname()

    const getContent = () => {
        if (showAuthenticatedToolTip) {
            return (
                <Content data-testid="product-summary-favourite-authenticated-tooltip">
                    {text.labels.favouritesAddedInfo}
                    <br />
                    <Capitalize> {getSoftLoginFirstname()} </Capitalize>
                    {text.labels.apostrophe} <strong>{text.labels.favouritesList}</strong>
                    <UserSection>
                        {text.labels.not} <Capitalize>{getSoftLoginFirstname()}</Capitalize>
                        {text.labels.questionSymbol} <Link href={getAccountPage(baseUrl)}>{text.buttons.signOut}</Link>
                    </UserSection>
                </Content>
            )
        }

        if (showAnonymousToolTip) {
            return (
                <Content data-testid="product-summary-favourite-anonymous-tooltip">
                    <Link href={getAccountPage(baseUrl)}>{text.buttons.signIn}</Link> {text.labels.or}{" "}
                    <Link href={getRegisterPage(baseUrl)}>{text.buttons.register}</Link>{" "}
                    {text.labels.favouritesSavePermanently}
                </Content>
            )
        }
    }

    useEffect(() => {
        if (isProductFavourited && favState === FavouriteState.Active) {
            if (showAuthenticatedToolTip || showAnonymousToolTip) {
                startTransition(() => {
                    setToolTipVisible(true)
                })
            }
        }
    }, [isProductFavourited, favState, showAuthenticatedToolTip, showAnonymousToolTip])

    const handleClose = () => {
        setToolTipVisible(false)
        setFavClicked(false)
    }

    useModalsCloseObservable(handleClose)

    if (!toolTipVisible) return null
    return (
        <ToolTip
            handleClose={handleClose}
            referenceElement={referenceElement}
            boundaryElement={document?.querySelector(`#${PLP_ENTRYPOINT}`)}
        >
            <Container>
                <Header>
                    <Title>{text.product.favourites}</Title>
                    <CloseButton onClick={handleClose} data-testid="product-summary-favourite-tooltip-close">
                        <CloseImage
                            src={`${env.REACT_APP_BLOB_STORAGE_PATH}/icons/shared/close.svg`}
                            alt={text.labels.continueShopping}
                        />
                    </CloseButton>
                </Header>
                {getContent()}
            </Container>
        </ToolTip>
    )
}

export default FavoritesToolTip
